import { useEffect } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import SubmissionStatus from '../../Data/SubmissionStatus';
import {
  setFirstSubmissionAttemptTimestamp,
  setPartialSubmissionStatus,
  setBsaSubmissionStatus,
  setResubmit,
  setSubmissionStatus,
  updateSelectAndConnectBrandSubmissionStatus,
} from '../../Lib/redux/slices/submission';

const triggerRetry = ({
  dispatch,
  isResubmission,
  isSelectAndConnectResubmission,
  selectAndConnectBrands,
  isPartialSubmission,
  isBsaSubmission,
}) => {
  if (isSelectAndConnectResubmission) {
    const selectAndConnectBrandsAwaitingRetry = Object.values(
      selectAndConnectBrands
    ).filter(
      (brand) =>
        brand.submissionStatus ===
        SubmissionStatus.AWAITING_RETRY_SELECT_AND_CONNECT_RESUBMISSION
    );

    selectAndConnectBrandsAwaitingRetry.forEach((brand) => {
      dispatch(
        updateSelectAndConnectBrandSubmissionStatus({
          brandId: brand.brandId,
          submissionStatus: SubmissionStatus.NOT_STARTED,
        })
      );
    });
    return;
  }

  if (isPartialSubmission) {
    dispatch(
      setPartialSubmissionStatus({
        partialSubmissionStatus: SubmissionStatus.AWAITING_PARTIAL_SUBMISSION,
      })
    );
    return;
  }

  if (isBsaSubmission) {
    dispatch(
      setBsaSubmissionStatus({
        bsaSubmissionStatus: SubmissionStatus.AWAITING_BSA_SUBMISSION,
      })
    );
    return;
  }

  if (isResubmission) {
    batch(() => {
      dispatch(setResubmit({ shouldResubmit: true }));
      setSubmissionStatus({
        submissionStatus: SubmissionStatus.COMPLETE,
      });
    });
  } else {
    dispatch(
      setSubmissionStatus({
        submissionStatus: SubmissionStatus.NOT_STARTED,
      })
    );
  }
};

const useSubmissionRetries = () => {
  const dispatch = useDispatch();
  const { isFinalPageBeforeSubmission } = useSelector(
    (store) => store.navigation
  );

  const {
    submissionStatus,
    submissionRetryTimestamp,
    firstSubmissionAttemptTimestamp,
    selectAndConnectBrands,
    partialSubmissionStatus,
    bsaSubmissionStatus,
  } = useSelector((store) => store.submission);

  const isResubmission =
    submissionStatus === SubmissionStatus.AWAITING_RETRY_RESUBMISSION;

  const isSelectAndConnectResubmission =
    submissionStatus ===
    SubmissionStatus.AWAITING_RETRY_SELECT_AND_CONNECT_RESUBMISSION;

  const isPartialSubmission =
    partialSubmissionStatus ===
    SubmissionStatus.AWAITING_RETRY_PARTIAL_SUBMISSION;

  const isBsaSubmission =
    bsaSubmissionStatus === SubmissionStatus.AWAITING_RETRY_BSA_SUBMISSION;

  const awaitingRetry =
    submissionStatus === SubmissionStatus.AWAITING_RETRY_SUBMISSION ||
    submissionStatus === SubmissionStatus.AWAITING_RETRY_RESUBMISSION ||
    submissionStatus ===
      SubmissionStatus.AWAITING_RETRY_SELECT_AND_CONNECT_RESUBMISSION ||
    partialSubmissionStatus ===
      SubmissionStatus.AWAITING_RETRY_PARTIAL_SUBMISSION ||
    isBsaSubmission;

  useEffect(() => {
    // This effect handles retrying submissions at the correct time.
    let retryTimeout;

    if (submissionRetryTimestamp && awaitingRetry) {
      const timeUntilRetry = submissionRetryTimestamp - Date.now();

      if (timeUntilRetry > 0) {
        retryTimeout = setTimeout(() => {
          triggerRetry({
            dispatch,
            isResubmission,
            isSelectAndConnectResubmission,
            selectAndConnectBrands,
            isPartialSubmission,
            isBsaSubmission,
          });
        }, timeUntilRetry);
      } else {
        triggerRetry({
          dispatch,
          isResubmission,
          isSelectAndConnectResubmission,
          selectAndConnectBrands,
          isPartialSubmission,
          isBsaSubmission,
        });
      }
    }

    return () => {
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
    };
  }, [
    submissionRetryTimestamp,
    awaitingRetry,
    dispatch,
    isResubmission,
    isSelectAndConnectResubmission,
    selectAndConnectBrands,
    isPartialSubmission,
    isBsaSubmission,
  ]);

  useEffect(() => {
    // If the customer navigates back during resubmissions cancel any pending retries
    if (
      !isFinalPageBeforeSubmission &&
      firstSubmissionAttemptTimestamp &&
      submissionStatus === SubmissionStatus.AWAITING_RETRY_SUBMISSION
    ) {
      dispatch(
        setFirstSubmissionAttemptTimestamp({
          firstSubmissionAttemptTimestamp: null,
        })
      );
      dispatch(
        setSubmissionStatus({
          submissionStatus: SubmissionStatus.NOT_STARTED,
        })
      );
    }
  }, [
    isFinalPageBeforeSubmission,
    firstSubmissionAttemptTimestamp,
    dispatch,
    submissionStatus,
  ]);
};

export default useSubmissionRetries;
